import type { Page } from '@efdigital/ll-addon-data';
import { debounce } from '@efdigital/ll-addon-ui/modifiers/debounce';
import { EditRecord } from '@efdigital/ll-admin-v2/components/edit-record.gts';
import { type EditRecordSignature } from '@efdigital/ll-admin-v2/components/edit-record-base.gts';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import type { Store } from '@warp-drive/core';

import {
  estimatePageDuration,
  roundToNearest,
} from '../../lib/page-duration-estimate.ts';

export class EditPageCompletion extends Component<EditRecordSignature<Page>> {
  @service declare store: Store;

  get canComplete(): boolean {
    return this.args.item.canComplete ?? true;
  }

  get durationValue(): string {
    const duration = this.args.item.duration;
    return duration === null || duration === undefined ? '' : String(duration);
  }

  toggleCanComplete = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.canComplete = target.checked;
  };

  setDuration = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const value = target.value.trim();
    if (value === '') {
      this.args.item.duration = null;
    } else {
      const parsed = Number.parseInt(value, 10);
      this.args.item.duration = Number.isNaN(parsed) ? null : parsed;
    }
  };

  setDurationValue = (minutes: number) => {
    this.args.item.duration = minutes;
  };

  /**
   * Estimate duration based on page widgets.
   * Uses the estimatePageDuration utility function.
   */
  estimateDuration = (): void => {
    const widgets = this.args.item.widgets || [];
    const estimatedDuration = estimatePageDuration(widgets, this.store);
    const roundedDuration = roundToNearest(estimatedDuration, 5);
    this.args.item.duration = roundedDuration;
  };

  <template>
    <EditRecord
      @editLabel="Edit Completion"
      @item={{@item}}
      @context={{@context}}
      @maxDepth={{2}}
      ...attributes
    >
      <:form as |uniqueId isDisabled|>
        <fieldset disabled={{isDisabled}} class="mb-0">
          <legend class="gud-eyebrow">Completion</legend>

          <div class="form-check mb-3">
            <input
              type="checkbox"
              class="form-check-input"
              id="{{uniqueId}}-can-complete"
              checked={{this.canComplete}}
              {{on "change" this.toggleCanComplete}}
            />
            <label class="form-check-label" for="{{uniqueId}}-can-complete">
              Show completion checkbox
            </label>
            <div class="form-text">
              Allow participants to mark this page as complete. This affects
              module and cohort completion tracking.
            </div>
          </div>

          <div class="mb-3">
            <label for="{{uniqueId}}-duration" class="form-label">
              Effort (minutes)
            </label>
            <div class="input-group mb-2" role="group">
              <input
                type="number"
                class="form-control"
                id="{{uniqueId}}-duration"
                value={{this.durationValue}}
                min="0"
                step="1"
                {{debounce this.setDuration}}
              />
              {{#unless isDisabled}}
                <button
                  type="button"
                  class="btn btn-primary btn-sm px-3"
                  {{on "click" (fn this.setDurationValue 5)}}
                >
                  5
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-sm px-3"
                  {{on "click" (fn this.setDurationValue 10)}}
                >
                  10
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-sm px-3"
                  {{on "click" (fn this.setDurationValue 20)}}
                >
                  20
                </button>
                <button
                  type="button"
                  class="btn btn-primary btn-sm px-3"
                  {{on "click" (fn this.setDurationValue 60)}}
                >
                  60
                </button>
                <button
                  type="button"
                  class="btn btn-outline-primary btn-magical-border btn-sm rounded-end-1 px-3"
                  {{on "click" this.estimateDuration}}
                >
                  <iconify-icon icon="ph:sparkle" />
                  Estimate
                </button>
              {{/unless}}
            </div>
            <div class="form-text">
              Estimated time for a typical participant to complete this page (in
              minutes). This is used for reporting. Click "Estimate" to
              automatically calculate based on the widgets on the page.
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecord>
  </template>
}
